import { type CalendarDate, type Time, parseDate } from "@internationalized/date";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { stringToTimeValue, timeValueToString } from "@offstreetparking/design-system/forms/datetime/utils";
import { DAYS_OF_WEEK } from "@offstreetparking/domain/value";
import { DateTime } from "luxon";
import * as z from "zod/v4";
import type { WizardParkingChoice } from "../../PermitContextProvider/types";
import { hasNeverExpiresIntervals as checkNeverExpires } from "./hasNeverExpiresIntervals";

const FIELD_LABEL_MESSAGES = {
	startDate: msg`Start Date`,
	startTime: msg`Start Time`,
	endDate: msg`End Date`,
	endTime: msg`End Time`,
} satisfies Record<string, MessageDescriptor>;

export const makeTimeSelectionSchema = ({
	wizardSettings,
	timezone,
}: {
	wizardSettings: WizardParkingChoice["time"];
	timezone: string;
}) => {
	const neverExpires = checkNeverExpires(wizardSettings);
	const toLocationDateTime = ({ date, time }: { date: CalendarDate; time: Time }) =>
		DateTime.fromObject(
			{ year: date.year, month: date.month, day: date.day, hour: time.hour, minute: time.minute },
			{ zone: timezone },
		);

	return z
		.object({
			intervals: z.array(
				z.object({
					startDate: z.custom<CalendarDate>(),
					endDate: z.custom<CalendarDate>(),
					startTime: z.custom<Time>(),
					endTime: z.custom<Time>(),
					durationInMinutes: z.number().optional(),
				}),
			),
		})
		.superRefine((data, ctx) => {
			data.intervals.forEach((interval, index) => {
				if (!interval.startDate) {
					ctx.addIssue({
						code: "too_small",
						origin: "string",
						inclusive: true,
						exact: false,
						minimum: 1,
						params: { label: FIELD_LABEL_MESSAGES.startDate },
						path: ["intervals", index, "startDate"],
					});
				}
				if (!interval.startTime) {
					ctx.addIssue({
						code: "too_small",
						origin: "string",
						inclusive: true,
						exact: false,
						minimum: 1,
						params: { label: FIELD_LABEL_MESSAGES.startTime },
						path: ["intervals", index, "startTime"],
					});
				}
				if (!neverExpires && !interval.endDate) {
					ctx.addIssue({
						code: "too_small",
						origin: "string",
						inclusive: true,
						exact: false,
						minimum: 1,
						params: { label: FIELD_LABEL_MESSAGES.endDate },
						path: ["intervals", index, "endDate"],
					});
				}
				if (!neverExpires && !interval.endTime) {
					ctx.addIssue({
						code: "too_small",
						origin: "string",
						inclusive: true,
						exact: false,
						minimum: 1,
						params: { label: FIELD_LABEL_MESSAGES.endTime },
						path: ["intervals", index, "endTime"],
					});
				}

				if (interval.endDate && interval.startDate) {
					if (interval.endDate.compare(interval.startDate) < 0) {
						ctx.addIssue({
							code: "custom",
							params: {
								i18n: {
									key: "errors.before_date.inclusive",
									values: {
										date: interval.startDate.toString(),
									},
								},
							},
							path: ["intervals", index, "endDate"],
						});
					}

					if (interval.startDate && interval.endDate && interval.endTime && interval.startTime) {
						if (
							interval.endDate.compare(interval.startDate) === 0 &&
							interval.endTime.compare(interval.startTime) <= 0
						) {
							const endTimeDisabled =
								!wizardSettings.endTime.enabled || wizardSettings.stepSize !== undefined;
							ctx.addIssue({
								code: "custom",
								params: {
									i18n: endTimeDisabled
										? {
												key: "errors.after_time.not_inclusive",
												values: {
													date: interval.endTime?.toString() ?? "",
												},
											}
										: {
												key: "errors.before_time.not_inclusive",
												values: {
													date: interval.startTime?.toString() ?? "",
												},
											},
								},
								path: ["intervals", index, endTimeDisabled ? "startTime" : "endTime"],
							});
						}
					}
				}

				if (interval.startDate && wizardSettings.startDate.min && !wizardSettings.startDate.instant) {
					if (interval.startDate.compare(parseDate(wizardSettings.startDate.min)) < 0) {
						ctx.addIssue({
							code: "custom",
							params: {
								i18n: {
									key: "errors.before_date.inclusive",
									values: {
										date: wizardSettings.startDate.min,
									},
								},
							},
							path: ["intervals", index, "startDate"],
						});
					}
				}

				if (interval.startDate && wizardSettings.startDate.max && !wizardSettings.startDate.instant) {
					if (interval.startDate.compare(parseDate(wizardSettings.startDate.max)) > 0) {
						ctx.addIssue({
							code: "custom",
							params: {
								i18n: {
									key: "errors.after_date.inclusive",
									values: {
										date: wizardSettings.startDate.max,
									},
								},
							},
							path: ["intervals", index, "startDate"],
						});
					}
				}

				if (interval.startDate && wizardSettings.startDate.allowedDays) {
					const date = DateTime.fromObject({
						year: interval.startDate.year,
						month: interval.startDate.month,
						day: interval.startDate.day,
					});
					const dayName = DAYS_OF_WEEK[date.weekday - 1];
					if (dayName && !wizardSettings.startDate.allowedDays.includes(dayName)) {
						ctx.addIssue({
							code: "custom",
							params: {
								i18n: {
									key: "errors.invalid_day_of_week",
									values: {
										allowedDays: wizardSettings.startDate.allowedDays.map((x) => x.toLowerCase()),
									},
								},
							},
							path: ["intervals", index, "startDate"],
						});
					}
				}

				if (interval.endDate && wizardSettings.endDate.min && !wizardSettings.endDate.instant) {
					if (interval.endDate.compare(parseDate(wizardSettings.endDate.min)) < 0) {
						ctx.addIssue({
							code: "custom",
							params: {
								i18n: {
									key: "errors.before_date.inclusive",
									values: {
										date: wizardSettings.endDate.min,
									},
								},
							},
							path: ["intervals", index, "endDate"],
						});
					}
				}

				if (interval.endDate && wizardSettings.endDate.max && !wizardSettings.endDate.instant) {
					if (interval.endDate.compare(parseDate(wizardSettings.endDate.max)) > 0) {
						ctx.addIssue({
							code: "custom",
							params: {
								i18n: {
									key: "errors.after_date.inclusive",
									values: {
										date: wizardSettings.endDate.max,
									},
								},
							},
							path: ["intervals", index, "endDate"],
						});
					}
				}

				if (interval.endDate && wizardSettings.endDate.allowedDays) {
					const date = DateTime.fromObject({
						year: interval.endDate.year,
						month: interval.endDate.month,
						day: interval.endDate.day,
					});
					const dayName = DAYS_OF_WEEK[date.weekday - 1];
					if (dayName && !wizardSettings.endDate.allowedDays.includes(dayName)) {
						ctx.addIssue({
							code: "custom",
							params: {
								i18n: {
									key: "errors.invalid_day_of_week",
									values: {
										allowedDays: wizardSettings.endDate.allowedDays.map((x) => x.toLowerCase()),
									},
								},
							},
							path: ["intervals", index, "endDate"],
						});
					}
				}

				const startDateIsOnOrBeforeMinDate =
					interval.startDate &&
					(!wizardSettings.startDate.min ||
						interval.startDate.compare(parseDate(wizardSettings.startDate.min)) <= 0);

				if (
					!wizardSettings.timeNotInPast &&
					wizardSettings.startTime.min &&
					interval.startTime &&
					wizardSettings.startTime.min !== wizardSettings.startTime.max &&
					startDateIsOnOrBeforeMinDate
				) {
					const minTime = stringToTimeValue(wizardSettings.startTime.min)?.subtract({ minutes: 1 });
					if (minTime && (interval.startTime as Time).compare(minTime) <= 0) {
						ctx.addIssue({
							code: "custom",
							params: {
								i18n: {
									key: "errors.before_time.not_inclusive",
									values: { date: `${timeValueToString(minTime)}:00` },
								},
							},
							path: ["intervals", index, "startTime"],
						});
					}
				}

				if (
					wizardSettings.startTime.max &&
					interval.startTime &&
					wizardSettings.startTime.max !== wizardSettings.startTime.min
				) {
					const maxTime = stringToTimeValue(wizardSettings.startTime.max)?.add({ minutes: 1 });
					if (maxTime && (interval.startTime as Time).compare(maxTime) >= 0) {
						ctx.addIssue({
							code: "custom",
							params: {
								i18n: {
									key: "errors.after_time.not_inclusive",
									values: { date: `${timeValueToString(maxTime)}:00` },
								},
							},
							path: ["intervals", index, "startTime"],
						});
					}
				}

				if (
					wizardSettings.endTime.max &&
					interval.endTime &&
					wizardSettings.endTime.max !== wizardSettings.endTime.min
				) {
					const maxTime = stringToTimeValue(wizardSettings.endTime.max)?.add({ minutes: 1 });
					if (maxTime && (interval.endTime as Time).compare(maxTime) >= 0) {
						ctx.addIssue({
							code: "custom",
							params: {
								i18n: {
									key: "errors.after_time.not_inclusive",
									values: { date: `${timeValueToString(maxTime)}:00` },
								},
							},
							path: ["intervals", index, "endTime"],
						});
					}
				}

				if (
					wizardSettings.endTime.min &&
					interval.endTime &&
					wizardSettings.endTime.min !== wizardSettings.endTime.max
				) {
					const minTime = stringToTimeValue(wizardSettings.endTime.min)?.subtract({ minutes: 1 });
					if (minTime && (interval.endTime as Time).compare(minTime) <= 0) {
						ctx.addIssue({
							code: "custom",
							params: {
								i18n: {
									key: "errors.before_time.not_inclusive",
									values: { date: `${timeValueToString(minTime)}:00` },
								},
							},
							path: ["intervals", index, "endTime"],
						});
					}
				}

				if (interval.durationInMinutes !== undefined && interval.durationInMinutes < 0) {
					ctx.addIssue({
						code: "custom",
						params: {
							i18n: {
								key: "errors.duration_min.inclusive",
								values: {
									min: { minutes: 0 },
								},
							},
						},
						path: ["intervals", index, "durationInMinutes"],
					});
				}

				if (
					wizardSettings.duration.max &&
					interval.endDate &&
					interval.endTime &&
					interval.startDate &&
					interval.startTime
				) {
					const startDateTime = toLocationDateTime({ date: interval.startDate, time: interval.startTime });
					const endDateTime = toLocationDateTime({ date: interval.endDate, time: interval.endTime });
					const durationMinutes = endDateTime.diff(startDateTime).as("minutes");

					if (durationMinutes > wizardSettings.duration.max) {
						ctx.addIssue({
							code: "custom",
							params: {
								i18n: {
									key: "errors.duration_max.inclusive",
									values: {
										max: { minutes: wizardSettings.duration.max },
									},
								},
							},
							path: ["intervals", index, "durationInMinutes"],
						});
					}

					// Mirrors ExpiryMustBeOnStartDayRule: sub-24h duration cap + time-of-day expiry
					// cap implies the permit must end on the start day.
					const MINUTES_PER_DAY = 24 * 60;
					if (
						wizardSettings.duration.max < MINUTES_PER_DAY &&
						wizardSettings.endTime.max &&
						interval.endDate.compare(interval.startDate) > 0
					) {
						ctx.addIssue({
							code: "custom",
							params: {
								i18n: {
									key: "errors.after_date.inclusive",
									values: { date: interval.startDate.toString() },
								},
							},
							path: ["intervals", index, "endDate"],
						});
					}
				}

				if (
					wizardSettings.duration.min &&
					interval.endDate &&
					interval.endTime &&
					interval.startDate &&
					interval.startTime
				) {
					const isTruncationInEffect =
						wizardSettings.duration.max !== undefined &&
						wizardSettings.duration.min === wizardSettings.duration.max &&
						!!wizardSettings.endTime.max;

					const startDateTime = toLocationDateTime({ date: interval.startDate, time: interval.startTime });
					const endDateTime = toLocationDateTime({ date: interval.endDate, time: interval.endTime });
					const durationMinutes = endDateTime.diff(startDateTime).as("minutes");

					if (durationMinutes < wizardSettings.duration.min && !isTruncationInEffect) {
						ctx.addIssue({
							code: "custom",
							params: {
								i18n: {
									key: "errors.duration_min.inclusive",
									values: {
										min: { minutes: wizardSettings.duration.min },
									},
								},
							},
							path: ["intervals", index, "durationInMinutes"],
						});
					}
				}

				if (wizardSettings.duration.days !== undefined && interval.startDate && interval.endDate) {
					const expectedEndDate = interval.startDate.add({ days: wizardSettings.duration.days });

					if (interval.endDate.compare(expectedEndDate) !== 0) {
						ctx.addIssue({
							code: "custom",
							params: {
								i18n: {
									key: "errors.duration_days.mismatch",
									values: { days: wizardSettings.duration.days },
								},
							},
							path: ["intervals", index, "endDate"],
						});
					}
				}
			});
		});
};
